<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import { Camera, ChevronRight, FileText, LogOut, Settings, ShieldCheck, Truck } from "lucide-vue-next";
import { api } from "@/lib/api";
import { loadProfile, logoutDriver, session } from "@/lib/session";

const router = useRouter(); const saving = ref(false); const error = ref(""); const success = ref("");
const form = reactive({ nickname:"", phone:"", plateNumber:"", axleCount:6, fuelType:"diesel", vehicleAge:4, selfReportedLoad:30 });
const media = reactive<Record<string,string>>({});
const photos = [
  { key:"registrationMediaId", purpose:"registration", label:"行驶证主页" },
  { key:"leftSideMediaId", purpose:"vehicle_left", label:"车辆左侧全身" },
  { key:"rightSideMediaId", purpose:"vehicle_right", label:"车辆右侧全身" },
  { key:"cargoMediaId", purpose:"cargo", label:"车厢装载照" },
];

async function init() {
  try {
    const result = await loadProfile(); if (!result) return;
    const driver = result.driver; const vehicle = result.vehicle;
    form.nickname=String(driver.nickname||""); form.phone=String(driver.phone||"");
    form.plateNumber=String(vehicle.plateNumber||""); form.axleCount=Number(vehicle.axleCount||6);
    form.fuelType=String(vehicle.fuelType||"diesel"); form.vehicleAge=Number(vehicle.vehicleAge||4);
    form.selfReportedLoad=Number(vehicle.selfReportedLoad||30);
    for (const photo of photos) if (vehicle[photo.key]) media[photo.key]=String(vehicle[photo.key]);
  } catch (cause) { error.value = cause instanceof Error ? cause.message : "资料加载失败"; }
}

async function uploadPhoto(event: Event, key: string, purpose: string) {
  const input=event.target as HTMLInputElement; const file=input.files?.[0]; if(!file)return;
  saving.value=true; error.value="";
  try { const result=await api.upload<{id:string}>(file,purpose,session.driverToken); media[key]=result.id; }
  catch(cause){error.value=cause instanceof Error?cause.message:"上传失败"}
  finally{saving.value=false;input.value=""}
}

async function save() {
  saving.value=true;error.value="";success.value="";
  try {
    await api.put("/driver/me",{...form,...media},session.driverToken); await loadProfile(); success.value="车辆资料已保存，可参与一期任务";
  } catch(cause){error.value=cause instanceof Error?cause.message:"保存失败"}
  finally{saving.value=false}
}

function logout(){logoutDriver();router.replace("/login")}
onMounted(init);
</script>

<template>
  <section class="stack">
    <div class="profile-head card">
      <div class="avatar"><Truck :size="27" /></div><div class="grow"><h2>{{ form.nickname || "司机" }}</h2><p>{{ form.plateNumber || "请完善车辆资料" }} · 一微信绑定一辆车</p></div><span class="pill pill-success"><ShieldCheck :size="13" />资料后置审核</span>
    </div>
    <div class="card">
      <h3 class="section-title"><Truck :size="18" />车辆基础信息</h3>
      <div class="form-grid two">
        <label class="field"><span>微信昵称</span><input v-model="form.nickname" class="input" /></label>
        <label class="field"><span>手机号（选填）</span><input v-model="form.phone" class="input" inputmode="tel" placeholder="异常情况联系" /></label>
        <label class="field"><span>车牌号</span><input v-model="form.plateNumber" class="input" placeholder="粤B·12345" /></label>
        <label class="field"><span>轴数</span><select v-model.number="form.axleCount" class="select"><option :value="6">六轴（一期限定）</option></select></label>
        <label class="field"><span>燃料类型</span><select v-model="form.fuelType" class="select"><option value="diesel">柴油</option><option value="lng">LNG</option></select></label>
        <label class="field"><span>车龄（年）</span><input v-model.number="form.vehicleAge" class="input" type="number" min="0" max="40" /></label>
        <label class="field"><span>自报载重（吨）</span><input v-model.number="form.selfReportedLoad" class="input" type="number" step=".1" /></label>
      </div>
    </div>
    <div class="card">
      <h3 class="section-title"><Camera :size="18" />车辆实时照片</h3>
      <p class="small muted">请直接调用相机拍摄；不需要等待审核即可先领任务。</p>
      <div class="vehicle-photos">
        <label v-for="photo in photos" :key="photo.key" :class="{done:media[photo.key]}"><Camera :size="20" /><span>{{ photo.label }}</span><b>{{ media[photo.key] ? "已上传" : "去拍摄" }}</b><input type="file" accept="image/*" capture="environment" @change="uploadPhoto($event,photo.key,photo.purpose)" /></label>
      </div>
      <p v-if="error" class="error-banner">{{ error }}</p><p v-if="success" class="info-banner text-success">{{ success }}</p>
      <button class="btn btn-primary btn-block" :disabled="saving" @click="save">{{ saving ? "正在保存…" : "保存车辆资料" }}</button>
    </div>
    <div class="card menu-card">
      <RouterLink to="/rules"><FileText :size="19" /><span>参与规则与常见问题</span><ChevronRight :size="18" /></RouterLink>
      <a href="/admin"><Settings :size="19" /><span>运营管理后台</span><ChevronRight :size="18" /></a>
      <button @click="logout"><LogOut :size="19" /><span>退出当前账号</span><ChevronRight :size="18" /></button>
    </div>
  </section>
</template>

<style scoped>
.profile-head{display:flex;align-items:center;gap:12px}.profile-head h2{margin:0 0 4px;font-size:17px}.profile-head p{margin:0;color:var(--muted);font-size:11px}.avatar{width:48px;height:48px;display:grid;place-items:center;border-radius:15px;color:#fff;background:var(--primary)}.section-title{margin:0 0 15px;display:flex;gap:7px;align-items:center;font-size:15px}.vehicle-photos{margin:14px 0;display:grid;grid-template-columns:1fr 1fr;gap:9px}.vehicle-photos label{height:86px;display:grid;place-items:center;align-content:center;gap:3px;border:1px dashed #bec7d4;border-radius:11px;color:#6e798c;background:#fafbfd}.vehicle-photos label.done{color:#18865c;border-color:#77c7a9;background:#effaf5}.vehicle-photos span{font-size:11px}.vehicle-photos b{font-size:10px}.vehicle-photos input{display:none}.menu-card{padding:0 15px}.menu-card a,.menu-card button{width:100%;min-height:51px;padding:0;display:grid;grid-template-columns:26px 1fr 20px;align-items:center;border:0;border-bottom:1px solid var(--line);color:#465268;background:transparent;text-align:left;font-size:13px}.menu-card>*:last-child{border:0}
</style>
